/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  Dialog,
  IconButton,
  InlineNotification,
  LoadingSection,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import GroupAssignmentStep from 'bundles/enterprise-admin-learners/components/invite-learners/GroupAssignmentStep';
import IncompatibleContractGroupModal from 'bundles/enterprise-admin-learners/components/invite-learners/IncompatibleContractGroupModal';
import InviteOutcomeSummary from 'bundles/enterprise-admin-learners/components/invite-learners/InviteOutcomeSummary';
import {
  MAX_EMAIL_INVITE_ROWS,
  createEmptyEmailInviteRow,
  getEmailInviteRowErrors,
} from 'bundles/enterprise-admin-learners/components/invite-learners/emailInviteValidation';
import type {
  EmailInviteRow,
  EmailInviteRowErrors,
} from 'bundles/enterprise-admin-learners/components/invite-learners/emailInviteValidation';
import type { Invitee } from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import type { InviteLearnersResult } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useInviteLearners } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useRosterIdForContract } from 'bundles/enterprise-admin-learners/hooks/useRosterIdForContract';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  subtitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  rowList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  row: css`
    display: flex;
    align-items: flex-end;
    gap: var(--cds-spacing-200);
  `,
  fields: css`
    display: flex;
    align-items: flex-end;
    flex: 1;
    gap: var(--cds-spacing-200);
  `,
  field: css`
    flex: 1;
  `,
  deleteButtonSpacer: css`
    width: var(--cds-spacing-600);
    flex-shrink: 0;
  `,
  fieldsHeader: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  addRowButton: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-100);
  `,
  primaryAction: css`
    margin-right: var(--cds-spacing-150);
  `,
  csvNotification: css`
    margin-bottom: var(--cds-spacing-300);
  `,
};

export type EmailInviteModalProps = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** Set when the group is already known (Group Details lock, single-group admin) — skips GroupAssignmentStep. */
  autoSelectedGroupId?: string;
  /**
   * True for org admins with no group already known (Home/All Learners, not a Group
   * admin). Submits immediately with `orgId` once validated, then offers group
   * assignment as an optional post-invite step rather than gating submission on it.
   * Mutually exclusive with `autoSelectedGroupId` by construction — see
   * `getAutoSelectedGroupId`.
   */
  isOrgAdminNoGroup?: boolean;
  /** Switches to the CSV method — offered once the row limit is reached. */
  onSwitchToCsv: () => void;
  /** Fired once `inviteLearners()` resolves successfully, so callers can refetch. */
  onInvited: () => void;
  /** Set for multi-contract orgs once ContractSelectionStep has run — scopes the invite to that contract's roster. */
  selectedContractId?: string;
};

/**
 * Email/manual invite flow: up to 10 rows of { name, email }, validated on submit.
 * A pre-selected group (`autoSelectedGroupId`) skips GroupAssignmentStep and submits
 * immediately; `isOrgAdminNoGroup` also submits immediately (with `orgId`) but then
 * offers an optional, skippable group-assignment step; otherwise the mandatory picker
 * is shown after validation (Group admin, no group known).
 */
const EmailInviteModal = ({
  open,
  onClose,
  onBack,
  autoSelectedGroupId,
  isOrgAdminNoGroup,
  onSwitchToCsv,
  onInvited,
  selectedContractId,
}: EmailInviteModalProps) => {
  const isAddToGroupFlow = Boolean(autoSelectedGroupId);
  const org = useThirdPartyOrganization();
  const [rows, setRows] = React.useState<EmailInviteRow[]>(() => [createEmptyEmailInviteRow()]);
  const [rowErrors, setRowErrors] = React.useState<Record<string, EmailInviteRowErrors>>({});
  const [validatedInvitees, setValidatedInvitees] = React.useState<Invitee[] | null>(null);
  const [isCsvNotificationDismissed, setIsCsvNotificationDismissed] = React.useState(false);
  const isAtMaxRows = rows.length >= MAX_EMAIL_INVITE_ROWS;
  const { inviteLearners, submitting } = useInviteLearners();
  const [outcome, setOutcome] = React.useState<InviteLearnersResult | 'error' | null>(null);
  // True once the org-level (no-group) send has succeeded and the optional,
  // post-invite GroupAssignmentStep should be offered instead of the outcome screen.
  const [showPostSendGroupStep, setShowPostSendGroupStep] = React.useState(false);
  const [postSendGroupError, setPostSendGroupError] = React.useState('');
  const hasAutoSubmitted = React.useRef(false);

  // Set whenever a group has been chosen (or auto-selected) and we're resolving its
  // roster under `selectedContractId` before the mutation actually fires — a no-op,
  // effectively-synchronous step for single-contract orgs (see useRosterIdForContract).
  const [rosterLookupGroupId, setRosterLookupGroupId] = React.useState<string | undefined>(undefined);
  const [pendingSubmitKind, setPendingSubmitKind] = React.useState<'invite' | 'postSend' | null>(null);
  const [showIncompatibleContractModal, setShowIncompatibleContractModal] = React.useState(false);
  const {
    rosterId: resolvedRosterId,
    loading: rosterLoading,
    hasNoMatch,
  } = useRosterIdForContract(rosterLookupGroupId, selectedContractId);
  const isResolvingRoster = Boolean(pendingSubmitKind);

  const submitInvite = React.useCallback<(groupId: string, rosterId?: string) => Promise<void>>(
    async (groupId, rosterId) => {
      try {
        const result = await inviteLearners({ programId: groupId, rosterId, invitees: validatedInvitees ?? [] });
        setOutcome(result);
        onInvited();
      } catch {
        setOutcome('error');
      }
    },
    [inviteLearners, validatedInvitees, onInvited]
  );

  // Learners are already invited by this point (Auto-Join), so this creates a program
  // membership rather than sending a second invitation email.
  const submitPostSendGroup = React.useCallback<(groupId: string, rosterId?: string) => Promise<void>>(
    async (groupId, rosterId) => {
      setPostSendGroupError('');
      try {
        const result = await inviteLearners({ programId: groupId, rosterId, invitees: validatedInvitees ?? [] });
        if (result.failed.length > 0) {
          // The initial invite already succeeded — don't let a group-assignment failure
          // overwrite that outcome; surface it as this step's own error instead.
          setPostSendGroupError(_t('We couldn’t add the selected learners to the group. Please try again.'));
          return;
        }
        setOutcome(result);
        setShowPostSendGroupStep(false);
        onInvited();
      } catch {
        setPostSendGroupError(_t('We couldn’t add the selected learners to the group. Please try again.'));
      }
    },
    [inviteLearners, validatedInvitees, onInvited]
  );

  // Resolve the contract-scoped roster (if any) before either mutation call fires.
  React.useEffect(() => {
    if (!rosterLookupGroupId || !pendingSubmitKind || rosterLoading) return;
    const groupId = rosterLookupGroupId;
    const kind = pendingSubmitKind;
    setRosterLookupGroupId(undefined);
    setPendingSubmitKind(null);
    if (hasNoMatch) {
      setShowIncompatibleContractModal(true);
      return;
    }
    if (kind === 'invite') submitInvite(groupId, resolvedRosterId);
    else submitPostSendGroup(groupId, resolvedRosterId);
  }, [
    rosterLookupGroupId,
    pendingSubmitKind,
    rosterLoading,
    hasNoMatch,
    resolvedRosterId,
    submitInvite,
    submitPostSendGroup,
  ]);

  const handleInvite = React.useCallback<(groupId: string) => void>((groupId) => {
    setRosterLookupGroupId(groupId);
    setPendingSubmitKind('invite');
  }, []);

  const handlePostSendGroupSubmit = React.useCallback<(groupId: string) => void>((groupId) => {
    setRosterLookupGroupId(groupId);
    setPendingSubmitKind('postSend');
  }, []);

  // Org admin, no group known: the invite already goes out here — group assignment
  // (below) is a separate, optional step afterward, not a gate on this submission.
  const handleOrgLevelInvite = React.useCallback<() => Promise<void>>(async () => {
    try {
      const result = await inviteLearners({ orgId: org.id, invitees: validatedInvitees ?? [] });
      setOutcome(result);
      // Only offer the optional group step when someone was actually invited/joined —
      // otherwise this would claim "invites are on their way" for a total failure.
      if (result.successful.length + result.membershipEmails.length > 0) setShowPostSendGroupStep(true);
      onInvited();
    } catch {
      setOutcome('error');
    }
  }, [inviteLearners, validatedInvitees, org.id, onInvited]);

  const handleSkipPostSendGroupStep = () => setShowPostSendGroupStep(false);

  React.useEffect(() => {
    if (!isAtMaxRows) setIsCsvNotificationDismissed(false);
  }, [isAtMaxRows]);

  React.useEffect(() => {
    // Reset on close so a later reopen never briefly shows a stale outcome/group-picker
    // screen before the parent's own step-reset effect swaps this component out.
    if (!open) {
      setRows([createEmptyEmailInviteRow()]);
      setRowErrors({});
      setValidatedInvitees(null);
      setOutcome(null);
      setShowPostSendGroupStep(false);
      setPostSendGroupError('');
      setRosterLookupGroupId(undefined);
      setPendingSubmitKind(null);
      setShowIncompatibleContractModal(false);
      hasAutoSubmitted.current = false;
    }
  }, [open]);

  React.useEffect(() => {
    // Skip path: group already known, submit immediately once invitees are validated.
    if (!validatedInvitees || hasAutoSubmitted.current) return;
    if (autoSelectedGroupId) {
      hasAutoSubmitted.current = true;
      handleInvite(autoSelectedGroupId);
    } else if (isOrgAdminNoGroup) {
      hasAutoSubmitted.current = true;
      handleOrgLevelInvite();
    }
  }, [validatedInvitees, autoSelectedGroupId, isOrgAdminNoGroup, handleInvite, handleOrgLevelInvite]);

  const updateRow = (id: string, field: 'name' | 'email', value: string) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setRowErrors((prevErrors) => {
      const fieldErrorKey = field === 'name' ? 'nameError' : 'emailError';
      if (!prevErrors[id]?.[fieldErrorKey]) return prevErrors;
      const { [fieldErrorKey]: _removed, ...remainingRowErrors } = prevErrors[id];
      return { ...prevErrors, [id]: remainingRowErrors };
    });
  };

  const addRow = () => setRows((prevRows) => [...prevRows, createEmptyEmailInviteRow()]);

  const removeRow = (id: string) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id));
    setRowErrors((prevErrors) => {
      const { [id]: _removed, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  const handleSubmit = () => {
    const nextRowErrors = getEmailInviteRowErrors(rows);
    setRowErrors(nextRowErrors);
    if (Object.keys(nextRowErrors).length > 0) return;

    setValidatedInvitees(rows.map(({ name, email }) => ({ name: name.trim(), email: email.trim() })));
  };

  if (outcome && showPostSendGroupStep) {
    // Org admin, no group: the invite already went out (handleOrgLevelInvite above) —
    // this is now a purely optional, skippable step, not a gate on that submission.
    return (
      <React.Fragment>
        <GroupAssignmentStep
          open={open}
          onBack={() => setValidatedInvitees(null)}
          onSubmit={handlePostSendGroupSubmit}
          onSkip={handleSkipPostSendGroupStep}
          submitting={submitting || isResolvingRoster}
          errorMessage={postSendGroupError}
          skippable
        />
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          onClose={() => setShowIncompatibleContractModal(false)}
        />
      </React.Fragment>
    );
  }

  if (outcome) {
    return <InviteOutcomeSummary open={open} outcome={outcome} onClose={onClose} />;
  }

  if (validatedInvitees && (autoSelectedGroupId || isOrgAdminNoGroup)) {
    // Skip path: group already known (or none needed yet), waiting on the auto-submit
    // effect above to resolve.
    return (
      <React.Fragment>
        <Dialog open={open} onClose={() => {}} width="large">
          <HeadingGroup>
            {isAddToGroupFlow ? _t('Add learners via email') : _t('Invite learners via email')}
          </HeadingGroup>
          <Content>
            <LoadingSection label={_t('Adding your learners…')} />
          </Content>
          <Actions />
        </Dialog>
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          // No group picker to fall back to here (the group is auto-selected) — send the
          // admin back to invitee entry so they can retry with a different contract.
          onClose={() => {
            setShowIncompatibleContractModal(false);
            setValidatedInvitees(null);
          }}
        />
      </React.Fragment>
    );
  }

  if (validatedInvitees) {
    return (
      <React.Fragment>
        <GroupAssignmentStep
          open={open}
          onBack={() => setValidatedInvitees(null)}
          onSubmit={handleInvite}
          submitting={submitting || isResolvingRoster}
        />
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          onClose={() => setShowIncompatibleContractModal(false)}
        />
      </React.Fragment>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} width="large">
      <HeadingGroup>{isAddToGroupFlow ? _t('Add learners via email') : _t('Invite learners via email')}</HeadingGroup>
      <Content>
        <Typography2 component="p" color="supportText" css={styles.subtitle}>
          {isAddToGroupFlow
            ? _t("Enter the names and email addresses of the learners you'd like to add.")
            : _t("Enter the names and email addresses of the learners you'd like to invite.")}
        </Typography2>
        <div role="status">
          {isAtMaxRows && !isCsvNotificationDismissed && (
            <InlineNotification
              severity="information"
              onDismiss={() => setIsCsvNotificationDismissed(true)}
              inlineAction={
                <Button variant="ghost" size="small" onClick={onSwitchToCsv}>
                  {_t('Upload CSV')}
                </Button>
              }
              css={styles.csvNotification}
            >
              {_t('Adding more than 10 learners? A CSV upload will be faster.')}
            </InlineNotification>
          )}
        </div>
        <div css={[styles.row, styles.fieldsHeader]}>
          <div css={styles.fields}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.field}>
              {_t('Email')}
            </Typography2>
            <Typography2 component="span" variant="subtitleMedium" css={styles.field}>
              {_t('Name')}
            </Typography2>
          </div>
          <span css={styles.deleteButtonSpacer} />
        </div>
        <div css={styles.rowList}>
          {rows.map((row, index) => (
            <div css={styles.row} key={row.id}>
              <div css={styles.fields}>
                <TextField
                  aria-label={_t('Email')}
                  placeholder={_t('John.doe@example.com')}
                  value={row.email}
                  validationStatus={rowErrors[row.id]?.emailError ? 'error' : undefined}
                  validationLabel={rowErrors[row.id]?.emailError || undefined}
                  onChange={(event) => updateRow(row.id, 'email', event.target.value)}
                  inputProps={{ 'aria-required': true }}
                  css={styles.field}
                  fullWidth
                />
                <TextField
                  aria-label={_t('Name')}
                  placeholder={_t('John Doe')}
                  value={row.name}
                  validationStatus={rowErrors[row.id]?.nameError ? 'error' : undefined}
                  validationLabel={rowErrors[row.id]?.nameError || undefined}
                  onChange={(event) => updateRow(row.id, 'name', event.target.value)}
                  inputProps={{ 'aria-required': true }}
                  css={styles.field}
                  fullWidth
                />
              </div>
              <IconButton
                variant="ghost"
                intent="delete"
                disabled={rows.length <= 1}
                aria-label={_t('Remove learner #{rowNumber}', { rowNumber: index + 1 })}
                onClick={() => removeRow(row.id)}
                hideTooltip
              />
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          type="button"
          size="small"
          icon={<AddIcon />}
          iconPosition="before"
          onClick={addRow}
          css={styles.addRowButton}
        >
          {_t('Add learners')}
        </Button>
      </Content>
      <Actions>
        <Button variant="primary" onClick={handleSubmit} css={styles.primaryAction}>
          {isAddToGroupFlow ? _t('Add to group') : _t('Invite')}
        </Button>
        <Button variant="secondary" onClick={onBack}>
          {_t('Back')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default EmailInviteModal;
